import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getWithTokenAPI } from "../../util/Api";
import { students_list_approved, students_list_autoapproved } from "../../util/Apiendpoits";
import { SafeToast } from "../Toast/ShowToast";
import DynamicTable from "../ReusableComponent/DynamicTable";
import TableSkeleton from "../ReusableComponent/TableSkeleton";
import { FiArrowLeft, FiRefreshCw, FiTrendingUp, FiDollarSign, FiUsers, FiCreditCard } from "react-icons/fi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#059669", "#2563eb", "#8b5cf6", "#f59e0b", "#ec4899", "#3b82f6"];

const InstitutionRevenue = () => {
  const { token, userId } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");

  // Fetch payments list combining auto-approved and approved APIs
  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      const instId = userId || "695f3f832929fa82230aaf33";
      const autoApprovedUrl = `${students_list_autoapproved}&institutionId=${instId}&limit=1000`;
      const approvedUrl = `${students_list_approved}&institutionId=${instId}&limit=1000`;

      const [resAuto, resApproved] = await Promise.all([
        getWithTokenAPI(token, autoApprovedUrl),
        getWithTokenAPI(token, approvedUrl),
      ]);

      const autoList = Array.isArray(resAuto?.data) ? resAuto.data : [];
      const approvedList = Array.isArray(resApproved?.data) ? resApproved.data : [];

      // Use ALL records from BOTH APIs — every transaction amount must be counted
      const allItems = [...autoList, ...approvedList];

      const formatted = allItems.map((item) => ({
        id: item._id,
        userid: item.userId?._id,
        name: item.userId?.name || "N/A",
        phone: item.userId?.phoneNumber || "N/A",
        state: item.userId?.state || "",
        city: item.userId?.city || "",
        examType: item.batchId?.examType?.value || "N/A",
        institution: item.institutionId?.nameofInstitution || "N/A",
        testPrice: Number(item.testPrice) || 0,
        paymentStatus: item.paymentStatus || "paid",
        transactionId: item.transactionId || "N/A",
        paymentMethod: item.paymentMethod || "RAZORPAY",
        dateObj: new Date(item.autoApprovedAt || item.createdAt || Date.now()),
        dateStr: item.autoApprovedAt
          ? new Date(item.autoApprovedAt).toLocaleDateString("en-GB")
          : item.createdAt
          ? new Date(item.createdAt).toLocaleDateString("en-GB")
          : "N/A",
      }));

      setPayments(formatted);
    } catch (error) {
      console.error("Fetch revenue data error:", error);
      SafeToast.error("Failed to load revenue data");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
  }, [token, userId]);

  // Aggregate stats
  const totalRevenue = useMemo(() => {
    return payments.reduce((acc, curr) => acc + curr.testPrice, 0);
  }, [payments]);

  const avgTransactionValue = useMemo(() => {
    if (payments.length === 0) return 0;
    return Math.round(totalRevenue / payments.length);
  }, [payments, totalRevenue]);

  // Chart Data 1: Revenue by Exam Type
  const revenueByExamData = useMemo(() => {
    const map = {};
    payments.forEach((item) => {
      const exam = item.examType || "Other";
      map[exam] = (map[exam] || 0) + item.testPrice;
    });
    return Object.keys(map).map((exam) => ({
      name: exam,
      revenue: map[exam],
    }));
  }, [payments]);

  // Chart Data 2: Monthly Revenue Trend
  const monthlyRevenueData = useMemo(() => {
    const map = {};
    payments.forEach((item) => {
      if (item.dateObj && !isNaN(item.dateObj.getTime())) {
        const monthYear = item.dateObj.toLocaleDateString("en-IN", {
          month: "short",
          year: "numeric",
        });
        map[monthYear] = (map[monthYear] || 0) + item.testPrice;
      }
    });

    return Object.keys(map).map((month) => ({
      month,
      revenue: map[month],
    }));
  }, [payments]);

  // Client-side search filtering
  const filteredPayments = useMemo(() => {
    if (!search.trim()) return payments;
    const query = search.toLowerCase().trim();
    return payments.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.phone.includes(query) ||
        item.city.toLowerCase().includes(query) ||
        item.examType.toLowerCase().includes(query) ||
        item.transactionId.toLowerCase().includes(query)
    );
  }, [payments, search]);

  const columns = [
    {
      key: "name",
      label: "Student Name",
      render: (row) => (
        <div>
          <strong style={{ color: "#0f172a", fontSize: "14px" }}>{row.name}</strong>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Phone Number",
      render: (row) => row.phone,
    },
    {
      key: "examType",
      label: "Exam Type",
      render: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "6px",
            backgroundColor: "#eff6ff",
            color: "#2563eb",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          {row.examType}
        </span>
      ),
    },
    {
      key: "testPrice",
      label: "Amount Paid",
      render: (row) => (
        <strong style={{ color: "#059669", fontSize: "15px" }}>
          ₹{row.testPrice}
        </strong>
      ),
    },
    {
      key: "paymentMethod",
      label: "Method",
      render: (row) => (
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
          {row.paymentMethod}
        </span>
      ),
    },
    {
      key: "paymentStatus",
      label: "Status",
      render: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "16px",
            backgroundColor: "#dcfce7",
            color: "#15803d",
            fontWeight: 600,
            fontSize: "12px",
            textTransform: "capitalize",
          }}
        >
          ● {row.paymentStatus}
        </span>
      ),
    },
    {
      key: "transactionId",
      label: "Transaction ID",
      render: (row) => (
        <span
          style={{
            fontFamily: "monospace",
            fontSize: "12px",
            backgroundColor: "#f1f5f9",
            padding: "4px 8px",
            borderRadius: "4px",
            border: "1px dashed #cbd5e1",
          }}
        >
          {row.transactionId}
        </span>
      ),
    },
    {
      key: "dateStr",
      label: "Date",
      render: (row) => row.dateStr,
    },
  ];

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", fontFamily: "'Alata', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              borderRadius: "8px",
              padding: "8px 14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "#475569",
              fontWeight: 500,
            }}
          >
            <FiArrowLeft size={18} /> Back
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: "22px", color: "#0f172a", fontWeight: 700 }}>
              Total Revenue & Payment History
            </h2>
            <span style={{ fontSize: "14px", color: "#64748b" }}>
              Analytics and complete payment records
            </span>
          </div>
        </div>
      </div>

      {/* Summary Cards Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "20px",
          marginBottom: "28px",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
            borderRadius: "14px",
            padding: "20px",
            color: "white",
            boxShadow: "0 4px 14px rgba(16, 185, 129, 0.2)",
          }}
        >
         <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "8px",
  }}
>
  <span
    style={{
      fontSize: "24px",
      fontWeight: "700",
      color: "#fff",
    }}
  >
    ₹
  </span>

  <span
    style={{
      fontSize: "14px",
      fontWeight: "600",
      color: "#fff",
      opacity: 0.9,
      textTransform: "uppercase",
    }}
  >
    Total Revenue
  </span>
</div>
          <div style={{ fontSize: "32px", fontWeight: 700 }}>{totalRevenue.toLocaleString()}</div>
        </div>

        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)",
            borderRadius: "14px",
            padding: "20px",
            color: "white",
            boxShadow: "0 4px 14px rgba(37, 99, 235, 0.2)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <FiUsers size={24} />
            <span style={{ fontSize: "14px", fontWeight: 600, opacity: 0.9 }}>PAID TRANSACTIONS</span>
          </div>
          <div style={{ fontSize: "32px", fontWeight: 700 }}>{payments.length}</div>
        </div>

        <div
          style={{
            background: "linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)",
            borderRadius: "14px",
            padding: "20px",
            color: "white",
            boxShadow: "0 4px 14px rgba(139, 92, 246, 0.2)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <FiTrendingUp size={24} />
            <span style={{ fontSize: "14px", fontWeight: 600, opacity: 0.9 }}>AVG TRANSACTION</span>
          </div>
          <div style={{ fontSize: "32px", fontWeight: 700 }}>₹{avgTransactionValue.toLocaleString()}</div>
        </div>
      </div>

      {/* Analytics Charts Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
          gap: "24px",
          marginBottom: "28px",
        }}
      >
        {/* Monthly Revenue Bar Chart */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            padding: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", color: "#0f172a" }}>
            Monthly Revenue Growth ₹
          </h3>
          {monthlyRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyRevenueData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]}
                  contentStyle={{ borderRadius: "8px", border: "1px solid #cbd5e1" }}
                />
                <Bar dataKey="revenue" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>No revenue trend data</div>
          )}
        </div>

        {/* Revenue by Exam Type Pie Chart */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            padding: "20px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
          }}
        >
          <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", color: "#0f172a" }}>
            Revenue Breakdown by Exam Type
          </h3>
          {revenueByExamData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={revenueByExamData}
                  cx="50%"
                  cy="50%"
                  outerRadius={85}
                  dataKey="revenue"
                  label={({ name, revenue }) => `${name}: ₹${revenue}`}
                >
                  {revenueByExamData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, "Revenue"]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>No exam revenue data</div>
          )}
        </div>
      </div>

      {/* Payment History Table */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "14px",
          padding: "20px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", color: "#0f172a" }}>
          Payment History
        </h3>

        {loading ? (
          <TableSkeleton columns={columns} rows={6} hasActions={false} />
        ) : (
          <DynamicTable
            columns={columns}
            data={filteredPayments}
            filters={[]}
            search={search}
            setSearch={setSearch}
            selectedFilters={{}}
            handleFilterChange={() => {}}
            selectedRows={[]}
            setSelectedRows={() => {}}
            onSearchClick={fetchRevenueData}
            activeTab="All"
          />
        )}
      </div>
    </div>
  );
};

export default InstitutionRevenue;
